import { AnalogsDelivery } from "../../../types/project";

export interface ProjectDto {
    id: string;
    name: string;
    organization_id: string;

    full_name: string;
    author_email: string;

    created_at: string;
    updated_at: string;
    started_at: string;
    last_time_scan_product: string | null;

    report_status: string;

    price: number;
    product_count: number;
    successed_product_count: number;
    canceled_product_count: number;
    report_ids: string[];

    avg_cost_of_delivery: number;
    processing_duration_per_hundred: number;
    has_archived_report: boolean;
    count_archived_products: number;
    count_archived_sources: number;
}

export interface CreateProjectResponse {
    project: ProjectDto;
    report: ReportDto;
}

export interface UploadPositionsResponse {
    products: ProductDto[];
    report: ReportDto;

    name_status: 'valid' | 'invalid';
    name_invalid_reason: string;
}

export interface ReportDto {
    id: string;
    status?: string;
    delivery_address?: string;
    delivery_processing_status?: string;
    project_id?: string;
    postal_code?: string;

    avg_price?: number;
    min_price?: number;
    max_price?: number;
    price?: number;

    avg_cost_of_delivery?: number;
    min_cost_of_delivery?: number;
    max_cost_of_delivery?: number;
    cost_of_delivery_of_all_products?: number;
    dict_of_deliveries?: Record<string, number>;
    delivery_status?: string;
    product_without_delivery_count?: number;

    total_count?: number;
    success_product_count?: number;
    products_with_offers_count?: number;
    weight?: number;
    volume?: number;
    delivery_postal_code?: string;

    branches_processing?: number;

    screenshots?: {
        status?: string;
        found?: number;
        total?: number;
    };
}

export interface KtruItem {
    code: string;
    name: string;
}

export interface SourceDto {
    id: string;
    title: string;
    url: string;
    price: number | null;
    selected: boolean;
    pics?: string;
    delivery_prices?: {
        delivery_service_id: number;
        price: number | null;
        params?: {
            box_size?: number[] | null;
            box_weight?: number | null;
            number_of_boxes?: number | null;
            last_box_size?: number[] | null;
            weight_last_box?: number | null;
        };
    }[];

    weight?: number | null;
    length?: number | null;
    width?: number | null;
    height?: number | null;
    gross_weight?: boolean;
    failed_to_calculate_dimensions?: boolean;
    dimensions_from?: string;

    delivery_info?: {
        address_from?: string;
        address_to?: string;
        city_from?: string;
        city_to?: string;
        index_from?: string;
        index_to?: string;
        weight?: number | null;
        length?: number | null;
        width?: number | null;
        height?: number | null;
        amount?: number | null;
    };
    delivery_index?: string;
    status_delivery_source?: string;
}

export interface ProductDeliveryStat {
    delivery_service_id: number;
    min_price: number;
    max_price: number;
    avg_price: number;
    total_price: number;
    count: number;

    outlier: 'high' | 'low' | null;
}

export interface ProductDimensionsDto {
    width: number;
    length: number;
    height: number;
    weight: number;
    source?: string | null;
    confirmed?: boolean;
    confidence?: number | null;
    reasoning?: string;
}

export interface TransportDimensionsDto {
    width: number;
    length: number;
    height: number;
    weight: number;
    packaging_class?: string | null;
    matches_main?: boolean;
    oversize?: boolean;
    is_manual?: boolean;
}

export interface ProductDto {
    id: string;
    report_id: string;
    organization_id: string;
    created_at: string;
    author_email: string;

    name: string;
    name_on_file: string;
    filtered_name: string;
    name_by_gpt: string | null;

    name_status: 'valid' | 'invalid';
    name_invalid_reason?: string;

    additional_characteristic: string;
    amount: number;
    unit: string;
    region_name: string;
    number: number;
    row: number;

    price_min: number | null;
    price_max: number | null;
    request_price?: number | null;
    search_analogs?: boolean;

    search_attempt?: number;
    must_words: string[];
    initial_urls: string[];

    status: string;
    start_searching: string | null;
    end_searching: string | null;
    found_at: number;
    reason: string;
    price: number;
    init_price: number;
    sources?: SourceDto[];
    min_price?: number;
    max_price?: number;
    avg_price?: number;
    selected_sources: unknown[];
    hosts: unknown[];
    urls: unknown[];
    blocked_hosts: string[];

    delivery_status: string;
    delivery_stats: ProductDeliveryStat[];
    min_cost_of_delivery: number;
    max_cost_of_delivery: number;
    avg_cost_of_delivery?: number;
    avg_cost_of_delivery_without_packaging?: number;
    cost_of_packaging?: number;

    width: number;
    height: number;
    length: number;
    weight: number;
    sources_with_incorrect_dimensions: number;
    apply_dimensions_to_all_product_sources: boolean;
    standard_size_id: string | null;

    dimensions?: ProductDimensionsDto;
    transport_dimensions?: TransportDimensionsDto;

    dimensions_origin?: string | null;
    dimensions_confirmed?: boolean;
    dimensions_confidence?: number | null;
    dimensions_reasoning?: string;

    category: string;
    ktru_list: KtruItem[];
    show_ktru_list: KtruItem[];

    hints: unknown[];
    pics: string;
    screenshot_folder_name: string;
    upload_batch_id: string;
    upload_product_to_report: boolean;
    semantic_search_used: boolean;
    cache_yandex: boolean;
    use_gpt: boolean;
    is_archived: boolean;
    is_duplicate: boolean;
    deleted: boolean;
    analogs_delivery?: AnalogsDelivery;
}

export interface SearchProductRequestDto {
    name: string;
    additional_characteristic?: string;
    amount?: number;
    unit?: string;
    region_name?: string;
    price_min?: number | null;
    price_max?: number | null;
    search_analogs?: boolean;
    logistics?: boolean;
    request_price?: number | null;
}

export type RestartProductBody = Partial<SearchProductRequestDto>;

export interface FixDimensionsBody {
    project_id: string;
    organization_id: string;
    product_id: string;
    name?: string;
    url?: string;
    weight: number;
    length: number;
    width: number;
    height: number;
    correct_weight: number;
    correct_length: number;
    correct_width: number;
    correct_height: number;
    transport_length?: number;
    transport_width?: number;
    transport_height?: number;
    transport_weight?: number;
}

export interface FixAnalogDimensionsBody {
    project_id: string;
    organization_id: string;
    product_id: string;
    analog_url: string;
    correct_weight: number;
    correct_length?: number;
    correct_width?: number;
    correct_height?: number;
    transport_length?: number;
    transport_width?: number;
    transport_height?: number;
    transport_weight?: number;
}

export interface StartedProductDto {
    id: string;
    name: string;
    status: string;
    is_duplicate: boolean;
    sources: SourceDto[];
    number: number;
}

export interface StartProductsSearchResponse {
    products: StartedProductDto[];
    report: { id: string; status: string };
}

export interface MarketplaceItemDto {
    marketplace: string;
    nm?: number;
    title?: string;
    brand?: string;
    price?: number | null;
    price_before_discount?: number | null;
    currency?: string;
    url?: string;
    in_stock?: boolean;
    rating?: number | null;
    match_why?: string;
}

export interface MarketplacesMetaDto {
    status?: string;
    matched?: number;
    marketplaces?: Record<string, { status?: string; reason?: string }>;
    search_engine?: string;
    processing_time?: string;
    updated_at?: string;
}

export interface SupplierPortalItemDto {
    name?: string;
    vendor?: string;
    price?: number | null;
    url?: string;
    sku_id?: number | null;
    currency?: string;
    match_why?: string;
}

export interface SupplierPortalMetaDto {
    status?: string;
    matched?: number;
    reason?: string;
    match_why?: string;
}

export interface ProductSupplierPortalDto {
    product_id?: string;
    supplier_portal?: SupplierPortalItemDto[];
    supplier_portal_meta?: SupplierPortalMetaDto;
}

export interface ProductMarketplacesDto {
    product_id: string;
    marketplaces: MarketplaceItemDto[];
    marketplaces_meta: MarketplacesMetaDto;
}

const FINISHED_STATUSES = new Set(['finished', 'canceled', 'error', 'failed']);

export type AnalogCharacteristicStatusDto = 'match' | 'mismatch' | 'no_data';

export interface AnalogCharacteristicDto {
    name: string;
    position_value: string;
    analog_value: string;
    status: AnalogCharacteristicStatusDto;
    source_url: string;
}

export interface AnalogCharacteristicsSummaryDto {
    match: number;
    mismatch: number;
    no_data: number;
}

export interface AnalogDto {
    title: string;
    brand?: string;
    model?: string;
    url?: string;
    seller?: string;
    price: number;
    currency?: string;
    availability?: string;
    image?: string;
    match?: string;
    match_pct?: number | null;
    price_delta_pct?: number | null;
    analog_of?: string;
    flags?: string[];

    width?: number;
    length?: number;
    height?: number;
    weight?: number;
    transport_width?: number;
    transport_length?: number;
    transport_height?: number;
    transport_weight?: number;
    packaging_class?: string | null;
    transport_matches_main?: boolean;
    transport_oversize?: boolean;
    dimensions_origin?: string | null;
    dimensions_confirmed?: boolean;
    dimensions_confidence?: number | null;
    dimensions_reasoning?: string | null;
    standard_size_id?: string | null;
    shipping_form?: string | null;
    shipping_places?: number | null;
    unit_length_m?: number | null;
    shipping_estimated?: boolean;
    dimensions_parsed?: {
        found?: boolean;
        sides_order?: string;
        method?: string;
        kind?: string;
        confidence?: number;
    };

    min_cost_of_delivery?: number;
    max_cost_of_delivery?: number;
    avg_cost_of_delivery?: number;
    avg_cost_of_delivery_without_packaging?: number;
    cost_of_packaging?: number;
    delivery_prices?: { delivery_service_id: number; price: number | null }[];
    delivery_stats?: ProductDeliveryStat[];

    delivery_info?: SourceDto['delivery_info'];
    delivery_index?: string;

    characteristics?: AnalogCharacteristicDto[];
    characteristics_summary?: AnalogCharacteristicsSummaryDto;
}

export interface AnalogsMetaDto {
    status?: string;
    logistics_status?: string;
    generated?: number;
    verified?: number;
    dropped?: Record<string, number>;
    notes?: string;
    updated_at?: string;
}

export interface ProductAnalogsDto {
    product_id?: string;
    search_analogs?: boolean;
    analogs?: AnalogDto[];
    analogs_meta?: AnalogsMetaDto;
}

export const isProductFinished = (dto: ProductDto): boolean =>
    FINISHED_STATUSES.has((dto.status ?? '').toLowerCase());
